export const TURN_BY_TURN_CONFIG = Object.freeze({
  headingWindowMeters: 20,
  minimumTurnAngleDegrees: 10,
  minimumCandidateSpacingMeters: 5,
  maximumCandidateDistanceMeters: 2,
});

export function resolveMapboxTurnCase(maneuver = {}) {
  const type = String(maneuver?.type || "").toLowerCase();
  const modifier = String(maneuver?.modifier || "").toLowerCase();
  if (["roundabout", "rotary", "exit roundabout", "exit rotary"].includes(type))
    return 5;
  if (type === "uturn" || modifier === "uturn") return 4;
  if (type === "merge") return 6;
  if (["on ramp", "off ramp", "fork", "end of road"].includes(type)) return 7;
  if (modifier.includes("left")) return 3;
  if (modifier.includes("right")) return 2;
  return 1;
}

export function isRelevantMapboxManeuver(maneuver = {}) {
  const type = String(maneuver?.type || "").toLowerCase();
  const modifier = String(maneuver?.modifier || "").toLowerCase();
  const bearingBefore = Number(maneuver?.bearing_before);
  const bearingAfter = Number(maneuver?.bearing_after);
  const bearingDelta =
    Number.isFinite(bearingBefore) && Number.isFinite(bearingAfter)
      ? Math.abs(((bearingAfter - bearingBefore + 540) % 360) - 180)
      : 0;
  if (type === "turn") {
    if (maneuver?.streetNameChanged) return true;
    if (["sharp left", "sharp right", "uturn"].includes(modifier)) return true;
    return ["left", "right", "slight left", "slight right"].includes(modifier) &&
      bearingDelta >= TURN_BY_TURN_CONFIG.minimumTurnAngleDegrees;
  }
  if (maneuver?.streetNameChanged && !["depart", "arrive"].includes(type)) {
    return true;
  }
  return [
    "roundabout",
    "rotary",
    "exit roundabout",
    "exit rotary",
    "uturn",
    "merge",
    "on ramp",
    "off ramp",
    "fork",
    "end of road",
    "new name",
  ].includes(type);
}

const GUIDANCE_LABELS = {
  en: { exit: "Exit", follow: "Follow", toward: "Toward", lane: "Use the", directions: { left: "left", right: "right", straight: "straight", "slight left": "slight left", "slight right": "slight right", "sharp left": "sharp left", "sharp right": "sharp right", uturn: "U-turn" } },
  es: { exit: "Salida", follow: "Sigue", toward: "Hacia", lane: "Usa el carril", directions: { left: "izquierdo", right: "derecho", straight: "recto", "slight left": "ligeramente a la izquierda", "slight right": "ligeramente a la derecha", "sharp left": "marcado a la izquierda", "sharp right": "marcado a la derecha", uturn: "cambio de sentido" } },
  fr: { exit: "Sortie", follow: "Suivre", toward: "Vers", lane: "Prendre la voie", directions: { left: "de gauche", right: "de droite", straight: "tout droit", "slight left": "légèrement à gauche", "slight right": "légèrement à droite", "sharp left": "fortement à gauche", "sharp right": "fortement à droite", uturn: "demi-tour" } },
  de: { exit: "Ausfahrt", follow: "Folgen Sie", toward: "Richtung", lane: "Nutzen Sie die", directions: { left: "linke", right: "rechte", straight: "geradeaus", "slight left": "leicht linke", "slight right": "leicht rechte", "sharp left": "scharf linke", "sharp right": "scharf rechte", uturn: "Wende-" } },
};

function getGuidanceLabels(language) {
  const languageKey = String(language || "en").toLowerCase().split("-")[0];
  return GUIDANCE_LABELS[languageKey] || GUIDANCE_LABELS.en;
}

function getBannerInstructions(step = {}) {
  const banners = step?.bannerInstructions ?? step?.banner_instructions;
  return Array.isArray(banners) ? banners : [];
}

function getLaneDirections(step = {}) {
  const lanes = (Array.isArray(step?.intersections) ? step.intersections : []).flatMap(
    (intersection) => Array.isArray(intersection?.lanes) ? intersection.lanes : [],
  );
  const bannerLanes = getBannerInstructions(step)
    .flatMap((banner) => [banner?.primary, banner?.secondary, banner?.sub])
    .flatMap((section) => Array.isArray(section?.components) ? section.components : [])
    .filter((component) => component?.type === "lane");
  const activeLanes = [
    ...lanes.filter((lane) => lane?.active || lane?.valid),
    ...bannerLanes.filter((lane) => lane?.active),
  ];
  return Array.from(new Set(activeLanes.flatMap((lane) => {
    if (lane?.active_direction) return [lane.active_direction];
    const selected = lane?.valid_indication
      ? [lane.valid_indication]
      : Array.isArray(lane?.directions) ? lane.directions
        : Array.isArray(lane?.indications) ? lane.indications : [];
    return selected.filter((direction) => direction && direction !== "none");
  })));
}

function appendUniqueGuidance(parts, value) {
  const text = String(value || "").trim();
  if (!text || parts.some((part) => part.toLocaleLowerCase() === text.toLocaleLowerCase())) return;
  parts.push(text);
}

function buildMapboxGuidanceInstruction(step, maneuver, language) {
  const labels = getGuidanceLabels(language);
  const parts = [];
  const banner = getBannerInstructions(step)[0];
  const bannerPrimary = String(banner?.primary?.text || "").trim();
  const bannerSecondary = String(banner?.secondary?.text || "").trim();
  const baseInstruction = String(maneuver?.instruction || "").trim();
  appendUniqueGuidance(parts, baseInstruction);
  if (bannerPrimary && !parts.some((part) => part.toLocaleLowerCase().includes(bannerPrimary.toLocaleLowerCase()))) {
    appendUniqueGuidance(parts, bannerPrimary);
  }
  if (bannerSecondary && !parts.some((part) => part.toLocaleLowerCase().includes(bannerSecondary.toLocaleLowerCase()))) {
    appendUniqueGuidance(parts, bannerSecondary);
  }

  const exits = String(step?.exits || "").trim();
  const refs = String(step?.ref || "").trim();
  const destinations = String(step?.destinations || "").trim();
  if (exits && !parts.some((part) => part.toLocaleLowerCase().includes(exits.toLocaleLowerCase()))) {
    appendUniqueGuidance(parts, `${labels.exit} ${exits}`);
  }
  if (refs && !parts.some((part) => part.toLocaleLowerCase().includes(refs.toLocaleLowerCase()))) {
    appendUniqueGuidance(parts, `${labels.follow} ${refs}`);
  }
  if (destinations && !parts.some((part) => part.toLocaleLowerCase().includes(destinations.toLocaleLowerCase()))) {
    appendUniqueGuidance(parts, `${labels.toward} ${destinations}`);
  }

  const laneDirections = getLaneDirections(step);
  if (laneDirections.length) {
    const localizedDirections = laneDirections.map((direction) =>
      labels.directions[String(direction).toLowerCase()] || direction,
    );
    const languageKey = String(language || "en").toLowerCase();
    const laneText = languageKey.startsWith("de")
      ? `${labels.lane} ${localizedDirections.join(" / ")} Fahrspur`
      : languageKey.startsWith("en")
        ? `${labels.lane} ${localizedDirections.join(" / ")} lane`
        : `${labels.lane} ${localizedDirections.join(" / ")}`;
    appendUniqueGuidance(parts, laneText);
  }
  return parts.join(". ");
}

function getBestManeuverIntersection(step, maneuver) {
  const intersections = Array.isArray(step?.intersections) ? step.intersections : [];
  if (!intersections.length) return null;
  const maneuverLocation = maneuver?.location;
  const maneuverLng = Number(maneuverLocation?.[0]);
  const maneuverLat = Number(maneuverLocation?.[1]);
  const hasManeuverLocation = Number.isFinite(maneuverLng) && Number.isFinite(maneuverLat);
  return intersections
    .filter((intersection) => Array.isArray(intersection?.location))
    .map((intersection) => {
      const hasLaneData = Array.isArray(intersection?.lanes) && intersection.lanes.length > 0;
      const distance = hasManeuverLocation
        ? Math.hypot(Number(intersection.location[0]) - maneuverLng, Number(intersection.location[1]) - maneuverLat)
        : 0;
      return { intersection, hasLaneData, distance };
    })
    .sort((a, b) => Number(b.hasLaneData) - Number(a.hasLaneData) || a.distance - b.distance)[0]?.intersection || null;
}

function getSelectedRoadClass(step, maneuver) {
  const intersection = getBestManeuverIntersection(step, maneuver);
  return String(intersection?.mapbox_streets_v8?.class || "").trim().toLowerCase();
}

export function buildTurnCandidatesFromMapboxRoutes(routes = [], language = "en") {
  const candidates = [];
  const seenManeuvers = new Set();

  (Array.isArray(routes) ? routes : []).forEach((route) => {
    (Array.isArray(route?.legs) ? route.legs : []).forEach((leg) => {
      const steps = Array.isArray(leg?.steps) ? leg.steps : [];
      steps.forEach((step, stepIndex) => {
        const maneuver = step?.maneuver || {};
        const previousStep = steps[stepIndex - 1];
        const previousStreetName = String(previousStep?.name || "").trim();
        const streetName = String(step?.name || "").trim();
        const streetNameChanged = Boolean(
          previousStreetName &&
          streetName &&
          previousStreetName.toLocaleLowerCase() !== streetName.toLocaleLowerCase(),
        );
        const bestIntersection = getBestManeuverIntersection(step, maneuver);
        const previousRoadClass = getSelectedRoadClass(previousStep, previousStep?.maneuver);
        const roadClass = String(bestIntersection?.mapbox_streets_v8?.class || "")
          .trim()
          .toLowerCase();
        const roadClassChanged = Boolean(
          previousRoadClass && roadClass && previousRoadClass !== roadClass,
        );
        const candidateManeuver = {
          ...maneuver,
          streetNameChanged:
            streetNameChanged || roadClassChanged || String(maneuver?.type || "").toLowerCase() === "new name",
        };
        const location = maneuver?.location || bestIntersection?.location;
        const longitude = Number(location?.[0]);
        const latitude = Number(location?.[1]);
        const laneDirections = getLaneDirections(step);
        const bannerInstructions = getBannerInstructions(step);
        const hasExitGuidance = Boolean(String(step?.exits || "").trim()) ||
          bannerInstructions.some((banner) =>
            [banner?.primary, banner?.secondary, banner?.sub].some((section) =>
              Array.isArray(section?.components) && section.components.some((component) => component?.type === "exit-number"),
            ),
          );
        const hasLaneGuidance = laneDirections.length > 0;
        const relevantManeuver = isRelevantMapboxManeuver(candidateManeuver);
        if (
          (!relevantManeuver && !hasExitGuidance && !hasLaneGuidance) ||
          !Number.isFinite(longitude) ||
          !Number.isFinite(latitude)
        )
          return;

        const key = [
          String(maneuver?.type || "").toLowerCase(),
          String(maneuver?.modifier || "").toLowerCase(),
          longitude.toFixed(5),
          latitude.toFixed(5),
        ].join(":");
        if (seenManeuvers.has(key)) return;
        seenManeuvers.add(key);
        candidates.push({
          longitude,
          latitude,
          durationSeconds: Number(step?.duration || 0),
          maneuver: {
            ...candidateManeuver,
            instruction: buildMapboxGuidanceInstruction(step, maneuver, language),
            previousStreetName,
            streetName,
            exits: String(step?.exits || "").trim(),
            roadReference: String(step?.ref || "").trim(),
            destinations: String(step?.destinations || "").trim(),
            laneDirections,
            intersectionClasses: Array.from(new Set(
              (Array.isArray(bestIntersection?.classes) ? bestIntersection.classes : []).map(String),
            )),
          },
        });
      });
    });
  });

  return candidates;
}

export function isGeneratedTurnByTurnPoint(point = {}) {
  return Boolean(
    point?.isGeneratedDirection ?? point?.is_generated_direction ?? false,
  );
}

export function getLineCoordinatesFromGeometry(geometry = {}) {
  if (String(geometry?.type || "") === "LineString") {
    return Array.isArray(geometry?.coordinates) ? [geometry.coordinates] : [];
  }
  if (String(geometry?.type || "") === "MultiLineString") {
    return Array.isArray(geometry?.coordinates) ? geometry.coordinates : [];
  }
  return [];
}

export function snapCoordinateToRouteLine(longitude, latitude, geometry) {
  const lines = getLineCoordinatesFromGeometry(geometry);
  if (
    !Number.isFinite(longitude) ||
    !Number.isFinite(latitude) ||
    !lines.length
  )
    return null;

  const metersPerLatitudeDegree = 111_320;
  const metersPerLongitudeDegree =
    metersPerLatitudeDegree * Math.cos((latitude * Math.PI) / 180);
  let closest = null;
  let previousLinesDistance = 0;
  lines.forEach((line, lineIndex) => {
    let lineDistance = 0;
    for (let index = 0; index < line.length - 1; index += 1) {
      const start = line[index] || [];
      const end = line[index + 1] || [];
      const startLng = Number(start[0]);
      const startLat = Number(start[1]);
      const endLng = Number(end[0]);
      const endLat = Number(end[1]);
      if (
        !Number.isFinite(startLng) ||
        !Number.isFinite(startLat) ||
        !Number.isFinite(endLng) ||
        !Number.isFinite(endLat)
      )
        continue;
      const startX = startLng * metersPerLongitudeDegree;
      const startY = startLat * metersPerLatitudeDegree;
      const endX = endLng * metersPerLongitudeDegree;
      const endY = endLat * metersPerLatitudeDegree;
      const pointX = longitude * metersPerLongitudeDegree;
      const pointY = latitude * metersPerLatitudeDegree;
      const deltaX = endX - startX;
      const deltaY = endY - startY;
      const lengthSquared = deltaX * deltaX + deltaY * deltaY || 1;
      const segmentLength = Math.sqrt(lengthSquared);
      const ratio = Math.max(
        0,
        Math.min(
          1,
          ((pointX - startX) * deltaX + (pointY - startY) * deltaY) /
            lengthSquared,
        ),
      );
      const projectedX = startX + ratio * deltaX;
      const projectedY = startY + ratio * deltaY;
      const distanceMeters = Math.hypot(
        pointX - projectedX,
        pointY - projectedY,
      );
      if (!closest || distanceMeters < closest.distanceMeters) {
        closest = {
          longitude: projectedX / metersPerLongitudeDegree,
          latitude: projectedY / metersPerLatitudeDegree,
          distanceMeters,
          lineIndex,
          segmentIndex: index,
          alongMeters:
            previousLinesDistance + lineDistance + ratio * segmentLength,
        };
      }
      lineDistance += segmentLength;
    }
    previousLinesDistance += lineDistance;
  });
  return closest;
}

export function buildTurnCandidatesFromMainGeometry(geometry = {}) {
  const coordinates = getLineCoordinatesFromGeometry(geometry)[0] || [];
  if (coordinates.length < 3) return [];
  if (
    coordinates.some(
      (coordinate) =>
        !Array.isArray(coordinate) ||
        !Number.isFinite(Number(coordinate[0])) ||
        !Number.isFinite(Number(coordinate[1])) ||
        Math.abs(Number(coordinate[1])) > 90,
    )
  )
    return [];
  const {
    headingWindowMeters,
    minimumTurnAngleDegrees,
    minimumCandidateSpacingMeters,
  } = TURN_BY_TURN_CONFIG;
  const earthRadiusMeters = 6_371_008.8;
  const distanceBetween = (first, second) => {
    const firstLatitude = (Number(first?.[1]) * Math.PI) / 180;
    const secondLatitude = (Number(second?.[1]) * Math.PI) / 180;
    const latitudeDelta = secondLatitude - firstLatitude;
    const longitudeDelta =
      ((Number(second?.[0]) - Number(first?.[0])) * Math.PI) / 180;
    const haversine = Math.min(
      1,
      Math.sin(latitudeDelta / 2) ** 2 +
        Math.cos(firstLatitude) *
          Math.cos(secondLatitude) *
          Math.sin(longitudeDelta / 2) ** 2,
    );
    return 2 * earthRadiusMeters * Math.asin(Math.sqrt(haversine));
  };
  const cumulativeDistances = [0];
  for (let index = 1; index < coordinates.length; index += 1) {
    cumulativeDistances[index] =
      cumulativeDistances[index - 1] +
      distanceBetween(coordinates[index - 1], coordinates[index]);
  }
  const coordinateAtDistance = (targetDistance) => {
    let low = 1;
    let high = cumulativeDistances.length - 1;
    while (low < high) {
      const middle = Math.floor((low + high) / 2);
      if (cumulativeDistances[middle] < targetDistance) low = middle + 1;
      else high = middle;
    }
    const segmentDistance =
      cumulativeDistances[low] - cumulativeDistances[low - 1];
    const ratio = segmentDistance
      ? (targetDistance - cumulativeDistances[low - 1]) / segmentDistance
      : 0;
    return [
      Number(coordinates[low - 1][0]) +
        (Number(coordinates[low][0]) - Number(coordinates[low - 1][0])) * ratio,
      Number(coordinates[low - 1][1]) +
        (Number(coordinates[low][1]) - Number(coordinates[low - 1][1])) * ratio,
    ];
  };
  const toLocalMeters = (coordinate, referenceLatitude) => ({
    x:
      Number(coordinate?.[0]) *
      111_320 *
      Math.cos((referenceLatitude * Math.PI) / 180),
    y: Number(coordinate?.[1]) * 111_320,
  });
  const candidates = [];
  let lastCandidateAlongMeters = -Infinity;
  const routeLengthMeters = cumulativeDistances[cumulativeDistances.length - 1];

  for (let index = 1; index < coordinates.length - 1; index += 1) {
    const currentCoordinate = coordinates[index];
    const referenceLatitude = Number(currentCoordinate?.[1]);
    if (!Number.isFinite(referenceLatitude)) continue;
    const alongMeters = cumulativeDistances[index];
    if (
      alongMeters < headingWindowMeters ||
      routeLengthMeters - alongMeters < headingWindowMeters ||
      alongMeters - lastCandidateAlongMeters < minimumCandidateSpacingMeters
    )
      continue;
    const previous = toLocalMeters(
      coordinateAtDistance(alongMeters - headingWindowMeters),
      referenceLatitude,
    );
    const current = toLocalMeters(currentCoordinate, referenceLatitude);
    const next = toLocalMeters(
      coordinateAtDistance(alongMeters + headingWindowMeters),
      referenceLatitude,
    );
    const incoming = { x: current.x - previous.x, y: current.y - previous.y };
    const outgoing = { x: next.x - current.x, y: next.y - current.y };
    const cross = incoming.x * outgoing.y - incoming.y * outgoing.x;
    const dot = incoming.x * outgoing.x + incoming.y * outgoing.y;
    const signedAngle = (Math.atan2(cross, dot) * 180) / Math.PI;
    if (Math.abs(signedAngle) < minimumTurnAngleDegrees) continue;
    const direction = signedAngle > 0 ? "left" : "right";
    candidates.push({
      longitude: Number(currentCoordinate[0]),
      latitude: Number(currentCoordinate[1]),
      alongMeters,
      maneuver: {
        type: "turn",
        modifier: `sharp ${direction}`,
        instruction: direction === "left" ? "Turn left" : "Turn right",
      },
    });
    lastCandidateAlongMeters = alongMeters;
  }
  return candidates;
}
